import { getLogger } from "rcd-cpp-logger";
import { getToken } from "../utils/authClient";

const logger = getLogger("WebSocketManager");

export interface WebSocketManagerConfig {
  heartbeatIntervalMs?: number;
  maxReconnectAttempts?: number;
  baseReconnectDelayMs?: number;
  maxReconnectDelayMs?: number;
  onMaxRetriesExceeded?: (refId: string) => void;
}

const DEFAULT_CONFIG: Required<Omit<WebSocketManagerConfig, "onMaxRetriesExceeded">> = {
  heartbeatIntervalMs: 300000,
  maxReconnectAttempts: 10,
  baseReconnectDelayMs: 1000,
  maxReconnectDelayMs: 30000,
};

export class WebSocketManager {
  private static instance: WebSocketManager | null = null;

  private config: Required<Omit<WebSocketManagerConfig, "onMaxRetriesExceeded">> &
    Pick<WebSocketManagerConfig, "onMaxRetriesExceeded">;

  private socket: WebSocket | null = null;
  private baseUrl: string | null = null;
  private currentRefId: string | null = null;
  private currentSubscriptionType: string | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private messageHandler: ((data: unknown) => void) | null = null;

  private reconnectAttempt = 0;
  private reconnectVersion = 0;
  private isIntentionalClose = false;
  private pendingSubscription: { refId: string; subscriptionType: string } | null = null;

  private constructor(config: WebSocketManagerConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  static getInstance(config?: WebSocketManagerConfig): WebSocketManager {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager(config);
    }
    return WebSocketManager.instance;
  }

  isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  getRefId(): string | null {
    return this.currentRefId;
  }

  setBaseUrl(url: string | null): void {
    this.baseUrl = url;
  }

  getBaseUrl(): string | null {
    return this.baseUrl;
  }

  onMessage(handler: (data: unknown) => void): void {
    this.messageHandler = handler;
  }

  removeMessageHandler(): void {
    this.messageHandler = null;
  }

  async connect(refId?: string): Promise<void> {
    const targetRefId = refId ?? null;
    if (this.isConnected() && this.currentRefId === targetRefId) {
      return;
    }

    if (this.socket) {
      this.disconnect();
    }

    this.currentRefId = targetRefId;
    this.reconnectAttempt = 0;
    await this.openSocket();
  }

  subscribe(refId: string, subscriptionType: string): void {
    this.currentRefId = refId;
    this.currentSubscriptionType = subscriptionType;
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      logger.debug(`WS not yet connected — queued subscribe for refId: ${refId}`);
      this.pendingSubscription = { refId, subscriptionType };
      return;
    }
    this.pendingSubscription = null;
    this.socket.send(JSON.stringify({ action: "subscribe", refId, subscriptionType }));
    this.startHeartbeat(refId);
  }

  unsubscribe(refId: string): void {
    if (this.pendingSubscription?.refId === refId) {
      this.pendingSubscription = null;
    }
    if (this.currentRefId === refId) {
      this.clearHeartbeat();
      this.currentRefId = null;
      this.currentSubscriptionType = null;
    }
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify({ action: "unsubscribe", refId }));
  }

  disconnect(): void {
    this.isIntentionalClose = true;
    this.reconnectVersion++;
    this.clearHeartbeat();

    if (this.socket) {
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.onopen = null;
      if (
        this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING
      ) {
        this.socket.close();
      }
      this.socket = null;
    }

    this.currentRefId = null;
    this.currentSubscriptionType = null;
    this.pendingSubscription = null;
    this.reconnectAttempt = 0;
    this.isIntentionalClose = false;
  }

  private async openSocket(): Promise<void> {
    const baseUrl = this.baseUrl;
    if (!baseUrl) {
      logger.error("WebSocket URL is not configured");
      return;
    }

    let accessToken: string;
    let thruCd: string;
    try {
      ({ accessToken, thruCd } = await getToken());
    } catch (e) {
      logger.error(`Failed to get auth token`, e);
      this.scheduleReconnect();
      return;
    }

    const baseParams = `Authorization=${encodeURIComponent(accessToken)}&cpp-thru-cd=${encodeURIComponent(thruCd)}`;
    const url = this.currentRefId
      ? `${baseUrl}/?${baseParams}&refId=${encodeURIComponent(this.currentRefId)}`
      : `${baseUrl}/?${baseParams}`;

    const ws = new WebSocket(url);
    this.socket = ws;
    this.isIntentionalClose = false;

    ws.onopen = () => {
      logger.info(`Connected`);
      this.reconnectAttempt = 0;
      const pending = this.pendingSubscription;
      const refId = pending?.refId ?? this.currentRefId;
      const subscriptionType = pending?.subscriptionType ?? this.currentSubscriptionType;
      if (this.socket && refId && subscriptionType) {
        this.pendingSubscription = null;
        this.currentRefId = refId;
        this.currentSubscriptionType = subscriptionType;
        this.socket.send(
          JSON.stringify({ action: "subscribe", refId, subscriptionType }),
        );
        this.startHeartbeat(refId);
      }
    };

    ws.onmessage = (event: MessageEvent) => {
      logger.debug(`Message received`);
      const parsed = this.parseMessage(event.data);
      if (parsed !== null && this.messageHandler) {
        this.messageHandler(parsed);
      }
    };

    ws.onerror = (event: Event) => {
      logger.error(`WebSocket error`, event);
    };

    ws.onclose = (event: CloseEvent) => {
      logger.info(
        `Closed — code: ${event.code}, intentional: ${this.isIntentionalClose}`,
        event.code,
        this.isIntentionalClose,
      );
      this.clearHeartbeat();
      this.socket = null;

      if (!this.isIntentionalClose) {
        this.scheduleReconnect();
      }
    };
  }

  private startHeartbeat(refId: string): void {
    this.clearHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      const { socket } = this;
      if (socket && socket.readyState === WebSocket.OPEN) {
        logger.debug(`Sending heartbeat for refId: ${refId}`);
        socket.send(JSON.stringify({ action: "heartbeat", refId }));
      }
    }, this.config.heartbeatIntervalMs);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempt >= this.config.maxReconnectAttempts) {
      logger.warn(`Max reconnect attempts ${this.config.maxReconnectAttempts} reached`);
      if (this.currentRefId) {
        this.config.onMaxRetriesExceeded?.(this.currentRefId);
      }
      return;
    }

    const capturedVersion = this.reconnectVersion;
    const delay = this.calcBackoffDelay(this.reconnectAttempt);
    this.reconnectAttempt++;

    logger.info(`Reconnect attempt ${this.reconnectAttempt} in ${delay}ms`);

    setTimeout(async () => {
      // If disconnect() was called while we were waiting, bail out
      if (this.reconnectVersion !== capturedVersion) {
        logger.debug(`Reconnect cancelled (disconnected)`);
        return;
      }
      await this.openSocket();
    }, delay);
  }

  private calcBackoffDelay(attempt: number): number {
    const exp = Math.min(
      this.config.baseReconnectDelayMs * Math.pow(2, attempt),
      this.config.maxReconnectDelayMs,
    );
    // Add jitter: random value in [0, exp * 0.2]
    const jitter = Math.random() * exp * 0.2;
    return Math.floor(exp + jitter);
  }

  private parseMessage(raw: string): unknown {
    try {
      let parsed = JSON.parse(raw);

      // Handle double-encoding (string inside string)
      if (typeof parsed === "string") {
        parsed = JSON.parse(parsed);
      }

      if (parsed && typeof parsed === "object" && "statusCode" in parsed) {
        if (!("body" in parsed)) {
          // Ack-only response (e.g. heartbeat) — no data to forward
          logger.debug(`Received API GW ack (statusCode: ${parsed.statusCode}), ignoring`);
          return null;
        }
        const body = parsed.body;
        return typeof body === "string" ? JSON.parse(body) : body;
      }

      return parsed;
    } catch (e) {
      logger.error("Failed to parse message", e);
      return null;
    }
  }
}
